import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from "@/components/ui/tooltip";
import TodoItem from "@/domain/entities/TodoItem";
import { cn } from "@/lib/utils";
import { useTranslation } from "react-i18next";

type TodoLineProps = {
    todo: TodoItem;
    handleCompletionToggle: (todoToUpdate: TodoItem) => Promise<void>;
    onFadeOutEnd: () => void;
};

// When done: strikethrough + fade out after 2s delay (300ms duration).
// Mobile equivalent: TodoListItem in TodoList.tsx (Animated.timing, same timings).
const DashboardTodoLine = ({
    todo,
    handleCompletionToggle,
    onFadeOutEnd,
}: TodoLineProps) => {
    const { t } = useTranslation();

    return (
        <div
            className={cn(
                "flex items-center gap-2 rounded-sm px-1 transition-opacity duration-300 hover:bg-black/10",
                todo.done && "line-through opacity-0",
            )}
            style={{
                transitionDelay: "2s",
            }}
            onTransitionEnd={(event) => {
                if (todo.done && event.target === event.currentTarget)
                    onFadeOutEnd();
            }}
        >
            <TooltipProvider delayDuration={100}>
                <Tooltip>
                    <TooltipTrigger asChild>
                        <Checkbox
                            checked={todo.done}
                            className="border-white/50 bg-surface-activeFg bg-transparent/10"
                            id={todo.id}
                            onCheckedChange={() => {
                                handleCompletionToggle(todo);
                            }}
                        />
                    </TooltipTrigger>
                    <TooltipContent>
                        {todo.done
                            ? t("todo.uncompleteTask")
                            : t("todo.completeTask")}
                    </TooltipContent>
                </Tooltip>
            </TooltipProvider>
            <Label
                className="leading-1.5 cursor-pointer pr-2"
                htmlFor={todo.id}
                onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault(); // prevents htmlFor from also triggering onCheckedChange
                    handleCompletionToggle(todo);
                }}
            >
                <span className={`${todo.done ? "line-through" : ""}`}>
                    {todo.taskSummary}
                </span>
            </Label>
        </div>
    );
};

export default DashboardTodoLine;
